import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendListaCasosActivo,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import mock_Json from "../../../dataJson/casosActivosUsuario.json";
import { formatearFecha } from "../../../components/common/helper/formatoFecha";
import {
  formatDescripcion,
  formatearMensajeBackend,
  ordenarCasosUsuarioBackend,
  procesarPropiedadMapeo,
  shouldRenderTicket,
} from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";

interface State {
  listaCasosActivo: any[];
  status: TypeStatus;
  processDefinitionIdDisplayName: string | null;
  processDefinitionIdVersion: string | null;
  isLoading: boolean;
  Success: boolean;
  Mensaje: string | null;
  error: string | null;
}

const initialState: State = {
  listaCasosActivo: [],
  processDefinitionIdDisplayName: "",
  processDefinitionIdVersion: "",
  status: "idle",
  isLoading: false,
  Success: false,
  Mensaje: "",
  error: null,
};
interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}
const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};
const listaCasosActivoSlice = createSlice({
  name: "listaCasosActivo",
  initialState,
  reducers: {
    setListaCasosActivo: (
      state,
      action: PayloadAction<{ casos: any[]; numPagina: number }>,
    ) => {
      const { casos, numPagina } = action.payload;

      // Si la página es 0, reemplazamos los datos, si no, concatenamos con los existentes
      if (numPagina === 0) {
        state.listaCasosActivo = casos;
      } else {
        state.listaCasosActivo = [...state.listaCasosActivo, ...casos];
      }
    },

    getListaCasosActivo: (state, action: PayloadAction<any[]>) => {
      state.listaCasosActivo = action.payload;
    },
    setProcessDefinitionIdDisplayName: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.processDefinitionIdDisplayName = action.payload;
    },
    setProcessDefinitionIdVersion: (state, action: PayloadAction<string>) => {
      state.processDefinitionIdVersion = action.payload;
    },

    setClearListaCasosActivo: (state, action: PayloadAction<any[]>) => {
      state = initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setResult: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
});

const procesarCasosActivos = async (casos: any[]) => {
  return await Promise.all(
    casos.map(async (caso) => {
      const {
        alarma,
        descripcion,
        ci,
        id,
        start,
        end_date,
        Prioridad,
        Nombre,
        NombreContrato,
        ticketRedmine,
      } = caso;
      let partesResumen = [];
      if (alarma) {
        partesResumen.push(`Titulo: ${alarma}`);
      }
            if (descripcion) {
              const value = formatDescripcion(descripcion);
              partesResumen.push(`Descripción: ${value}`);
            }
      if (ci && id) {
        partesResumen.push(`Ci: ${ci} - ${id}`);
      }
      if (Prioridad) {
        partesResumen.push(`Prioridad: ${Prioridad}`);
      }
      if (Nombre) {
        partesResumen.push(`Asignado: ${Nombre}`);
      }
      if (NombreContrato) {
        partesResumen.push(`Contrato: ${NombreContrato}`);
      }

      const renderTicketRedmine = shouldRenderTicket(ticketRedmine);

      if (renderTicketRedmine) {
        partesResumen.push(`Ticket Redmine Nro: ${ticketRedmine}`);
      }
      const resumen =
        partesResumen.length > 0
          ? partesResumen.join("\n\n")
          : "No pudimos obtener los datos del caso";

      return {
        ...caso,
        resumen,
        start: formatearFecha(start),
        end_date: formatearFecha(end_date),
      };
    }),
  );
};

export const setBackendListaCasosActivo =
  (
    Cantidad: number,
    NumPagina: number,
    options?: { signal?: AbortSignal }, // Agregamos un parámetro opcional con 'signal'
  ): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));

    try {
      const response: AxiosResponse = await BackendListaCasosActivo(
        Cantidad,
        NumPagina,
        options,
      );

      const isError =
        response.data?.Success === false ||
        (response.data?.ResultadoSolicitud !== undefined &&
          response.data?.ResultadoSolicitud !== 200);

      if (isError) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        dispatch(setSuccess(false));
      } else {
        let ordenarLista = [...(response.data.Data || [])];
        ordenarLista.sort(ordenarCasosUsuarioBackend);
        ordenarLista = procesarPropiedadMapeo(ordenarLista, "prioridad");
        const listaFormateada = await procesarCasosActivos(ordenarLista);

        dispatch(
          setListaCasosActivo({
            casos: listaFormateada,
            numPagina: NumPagina,
          }),
        );
        dispatch(setIsLoading(false));
        dispatch(setStatus("succeeded"));
        dispatch(setSuccess(true));

        if (!response.data) {
          response.data = { Data: [] };
        }
        response.data.Data = listaFormateada;
      }
      dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));

      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setSuccess(false));
      dispatch(setStatus("failed"));

      if (error.response?.status === 401) {
        dispatch(setStatus("noAutorice"));
        dispatch(
          setMensaje(
            formatearMensajeBackend(
              "Tu sesión ha expirado. Vuelve a iniciar sesión",
            ),
          ),
        );
      } else if (error.response) {
        // Hay respuesta del servidor, pero es otro error (400, 422, 500, etc.)
        console.error(
          "Error de servidor:",
          error.response.status,
          error.response.data,
        );

        const errorData = error.response.data;
        const mensajeBackend = errorData?.Mensaje;
        const resultadoSolicitud = errorData?.ResultadoSolicitud;

        if (
          mensajeBackend &&
          (resultadoSolicitud !== 200 || errorData?.Success === false)
        ) {
          dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
        } else {
          dispatch(
            setMensaje(
              `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
            ),
          );
        }
      } else if (error.request) {
        console.error("Error de conexión:", error.message);
        dispatch(
          setMensaje(
            "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
          ),
        );
      } else {
        console.error("Error de red o solicitud:", error.message);
        dispatch(
          setMensaje(
            "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
          ),
        );
      }
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
      if (getCurrentDomain() === "localhost") {
        dispatch(setIsLoading(true));
        let response: AxiosResponse = defaultResp;
        response.data = mock_Json;

        const mockData: any = mock_Json;
        const isErrorMock =
          mockData?.Success === false ||
          (mockData?.ResultadoSolicitud !== undefined &&
            mockData?.ResultadoSolicitud !== 200);

        if (isErrorMock) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          let ordenarLista = [...(mock_Json.Data || [])];
          ordenarLista.sort(ordenarCasosUsuarioBackend);
          ordenarLista = procesarPropiedadMapeo(ordenarLista, "prioridad");
          const listaFormateada = await procesarCasosActivos(ordenarLista);
          dispatch(
            setListaCasosActivo({
              casos: listaFormateada,
              numPagina: NumPagina,
            }),
          );
          dispatch(setIsLoading(false));
          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(true));

          if (!response.data) {
            response.data = { Data: [] };
          }
          response.data.Data = listaFormateada;
        }
        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
        const mock401 = await import("../../../dataJson/401.json");
        if (mock401.status === 401) {
          dispatch(setStatus("noAutorice"));
          dispatch(
            setMensaje(
              formatearMensajeBackend(
                "Tu sesión ha expirado. Vuelve a iniciar sesión",
              ),
            ),
          );
        }
      }
    }
  };

export const setBackendClearListaCasosActivo =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      let response: AxiosResponse = defaultResp;
      dispatch(
        setListaCasosActivo({
          casos: [],
          numPagina: 0,
        }),
      );
      dispatch(setProcessDefinitionIdDisplayName(""));
      dispatch(setProcessDefinitionIdVersion(""));
      dispatch(setIsLoading(false));
      dispatch(setStatus("idle"));
      dispatch(setSuccess(false));
      dispatch(setMensaje(""));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setStatus("failed"));
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const {
  setListaCasosActivo,
  setProcessDefinitionIdDisplayName,
  setProcessDefinitionIdVersion,
  getListaCasosActivo,
  setClearListaCasosActivo,
  setIsLoading,
  setStatus,
  setResult,
  setSuccess,
  setMensaje,
  setError,
} = listaCasosActivoSlice.actions;

export default listaCasosActivoSlice.reducer;
